import { useMemo } from 'preact/hooks'
import { useSearchspringContext } from 'libs/searchspring/fetchers/provider'
import { useOption } from 'libs/searchspring/hooks/useOption'
import Icon from '@/components/snippets/icon/icon'
import clsx from 'clsx'

const ELLIPSIS = 'ellipsis'
const START_BLOCK_SIZE = 5
const MIDDLE_WINDOW_SIZE = 4
const END_BLOCK_SIZE = 5

function getPageNumbers(activePage, totalPages) {
  if (totalPages <= START_BLOCK_SIZE + 1) {
    return Array.from({ length: totalPages }, (_, i) => i + 1)
  }

  const pages = [1]
  const startThreshold = START_BLOCK_SIZE
  const endThreshold = totalPages - END_BLOCK_SIZE + 1

  if (activePage < startThreshold) {
    for (let i = 2; i <= Math.min(START_BLOCK_SIZE, totalPages); i++) pages.push(i)
    if (totalPages > START_BLOCK_SIZE) {
      pages.push(ELLIPSIS)
      pages.push(totalPages)
    }
    return pages
  }

  if (activePage >= endThreshold) {
    const left = Math.max(2, totalPages - END_BLOCK_SIZE + 1)
    pages.push(ELLIPSIS)
    for (let i = left; i <= totalPages; i++) pages.push(i)
    return pages
  }

  const left = Math.max(2, activePage - (MIDDLE_WINDOW_SIZE - 1))
  const right = Math.min(totalPages - 1, left + MIDDLE_WINDOW_SIZE - 1)
  pages.push(ELLIPSIS)
  for (let i = left; i <= right; i++) pages.push(i)
  pages.push(ELLIPSIS)
  pages.push(totalPages)
  return pages
}

export function Pagination() {
  const { results, refine } = useSearchspringContext()

  const activePage = useOption('page', {
    defaultValue: 1,
    syncWithURL: true
  })

  const totalPages = results?.pagination?.totalPages ?? 1
  const pageNumbers = useMemo(() => getPageNumbers(activePage, totalPages), [activePage, totalPages])

  const handlePrevPage = () => {
    if (activePage <= 1) return
    refine('page', activePage - 1)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleNextPage = () => {
    if (activePage >= totalPages) return
    refine('page', activePage + 1)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handlePage = (page) => {
    if (page === activePage || page === ELLIPSIS) return
    refine('page', page)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  if (totalPages <= 1) return null

  const buttonClass = 'btn btn-primary btn-primary-alt btn-border btn-m lg:btn-l'

  return (
    <div class="flex w-full items-center justify-center">
      <div className="grid w-fit grid-cols-2 items-center gap-1 pb-3.5 md:grid-cols-pagination">
        <button
          type="button"
          className={clsx(buttonClass, 'w-full md:w-fit')}
          onClick={handlePrevPage}
          disabled={activePage <= 1}
          aria-label="Previous page"
        >
          <div class="h-[11px] w-[16px] children:size-full">
            <Icon id="arrow-left" />
          </div>
          <span>Previous</span>
        </button>
        <div className="flex items-center justify-center gap-x-0.5 md-max:col-[1/-1] md-max:row-[1]">
          {pageNumbers.map((page, i) =>
            page === ELLIPSIS ? (
              <span key={`ellipsis-${i}`} className="paragraph-b font-medium">
                ...
              </span>
            ) : (
              <button
                key={page}
                type="button"
                className={[buttonClass, 'btn-square', page === activePage ? 'active' : ''].join(' ')}
                onClick={() => handlePage(page)}
                aria-label={`Page ${page}`}
                aria-current={page === activePage ? 'page' : undefined}
              >
                {page}
              </button>
            )
          )}
        </div>
        <button
          type="button"
          className={clsx(buttonClass, 'w-full md:w-fit')}
          onClick={handleNextPage}
          disabled={activePage >= totalPages}
          aria-label="Next page"
        >
          <span>Next</span>
          <div class="h-[11px] w-[16px] children:size-full">
            <Icon id="arrow-right" />
          </div>
        </button>
      </div>
    </div>
  )
}
